import api from '@/services/api'

export default {
  namespaced: true,

  state: {
    projects: [],
    currentProject: null,
    loading: false,
    error: null
  },

  getters: {
    projects: state => state.projects,
    currentProject: state => state.currentProject,
    currentProjectId: state => state.currentProject?.id,
    getProjectByAcronym: (state) => (acronym) => {
      if (!acronym) return null
      return state.projects.find(p => 
        (p.acronym && p.acronym.toLowerCase() === acronym.toLowerCase())
      )
    },
    loading: state => state.loading,
    error: state => state.error
  },

  mutations: {
    SET_PROJECTS(state, projects) {
      state.projects = projects
    },
    SET_CURRENT_PROJECT(state, project) {
      state.currentProject = project
      // Also persist to localStorage for persistence across reloads
      if (project) {
        localStorage.setItem('currentProjectId', project.id)
      } else {
        localStorage.removeItem('currentProjectId')
      }
    },
    SET_LOADING(state, loading) {
      state.loading = loading
    },
    SET_ERROR(state, error) {
      state.error = error
    }
  },

  actions: {
    async fetchProjects({ commit }) {
      commit('SET_LOADING', true)
      commit('SET_ERROR', null)
      try {
        const projects = await api.getProjects()
        commit('SET_PROJECTS', projects)
        return projects
      } catch (error) {
        commit('SET_ERROR', error.message)
        throw error
      } finally {
        commit('SET_LOADING', false)
      }
    },

    async fetchProject(_, id) {
      try {
        const project = await api.getProject(id)
        return project
      } catch (error) {
        console.error('Error fetching project:', error)
        throw error
      }
    },

    async createProject({ commit, dispatch }, projectData) {
      commit('SET_LOADING', true)
      try {
        const project = await api.createProject(projectData)
        await dispatch('fetchProjects')
        return project
      } catch (error) {
        commit('SET_ERROR', error.message)
        throw error
      } finally {
        commit('SET_LOADING', false)
      }
    },

    async selectProject({ commit, dispatch, state }, projectOrId) {
      try {
        let project = projectOrId
        if (typeof projectOrId === 'number' || typeof projectOrId === 'string') {
          // If ID passed, find it in state or fetch it
          project = await api.getProject(projectOrId)
        }

        if (!project) {
          throw new Error('Project not found')
        }

        console.log('[projects] selectProject start', { projectOrId, currentId: state.currentProject?.id })
        
        // 1. Clear store FIRST
        console.log('[projects] selectProject dispatching clearAll')
        await dispatch('snapshots/clearAll', null, { root: true })
        
        // 2. Commit new project (updates localStorage)
        commit('SET_CURRENT_PROJECT', project)

        // 3. Update API interceptor state immediately
        // (localStorage set by commit, but let's be explicit if needed, 
        // though commit already handles localStorage.setItem('currentProjectId'))

        // 4. Trigger reload
        dispatch('loadRecentItems', null, { root: true })

        // Update user's last viewed project preference silently
        try {
            await api.updateUser({ last_project_id: project.id })
        } catch (e) {
            console.error('Failed to update user preference:', e)
        }

        // 5. Force parallel reload of all snapshot data for the new project
        try {
            console.log('[projects] forcing refreshAllInParallel for new project')
            await dispatch('snapshots/refreshAllInParallel', null, { root: true })
        } catch(e) {
            console.warn('[projects] refreshAllInParallel failed', e)
        }

        console.log('[projects] selectProject done')
        return project
      } catch (error) {
        console.error('Error selecting project:', error)
        throw error
      }
    },

    async initializeProject({ commit, dispatch, state }) {
      // Logic to determine which project to load on startup
      // 1. Check localStorage
      // 2. Check user preference (if logged in)
      // 3. Default to first project
      
      const storedId = localStorage.getItem('currentProjectId')
      let projectIdToLoad = storedId ? parseInt(storedId) : null

      if (!projectIdToLoad) {
        // fetch projects to see if we can pick one
        const projects = await dispatch('fetchProjects')
        if (projects.length > 0) {
            // Prefer one from user pref if available?
            // User pref is loaded via fetchUser.
            // But fetchUser might not be done yet? 
            // We'll rely on what's passed or default.
            projectIdToLoad = projects[0].id
        }
      } else {
          // ensure projects are loaded
          if (state.projects.length === 0) {
              await dispatch('fetchProjects')
          }
      }

      if (projectIdToLoad) {
          // verify it exists in list
          const project = state.projects.find(p => p.id === projectIdToLoad)
          
          // Only set if no project is currently selected (to avoid overwriting URL-based selection)
          if (!state.currentProject) {
            if (project) {
                commit('SET_CURRENT_PROJECT', project)
            } else if (state.projects.length > 0) {
                 // Fallback if stored ID not found
                 commit('SET_CURRENT_PROJECT', state.projects[0])
            }
          }
      }
    }
  }
}

